import { API } from 'aws-amplify';
import { GraphQLResult, GRAPHQL_AUTH_MODE } from '@aws-amplify/api';
import { authService } from '../../utils/services/auth-service';
import { extractPathIdentifier } from '../../utils/helpers/api-helpers';
import { COMMON_PAGE_PATHS } from '../../constans/url-constants';
import { Observable } from 'zen-observable-ts';
import { IGraphQLSubscriptionError } from '../../utils/services/error-parsing-service';
import { SUBSCRIPTION_CONNECTION_LOST_ERROR } from '../../constans/error-common-constants';
import { getErrorBoundaryStore } from '../../store/error-boundary-store/error-boundary-store-instanse';
import ErrorBoundaryStore from '../../store/error-boundary-store/error-boundary-store';
import { getMixpanelService } from '../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../constans/mixpanel-contants';
import { DEFAULT_WS_CONNECTION_RETRY_VALUE } from '../../constans/graphql-api-client-constants';

let retryAttemptUntilSendErrorTrack = DEFAULT_WS_CONNECTION_RETRY_VALUE;

interface IGraphqlOperationOptions {
    query: string;
    variables?: Record<string, unknown>;
    authToken?: string;
}

export interface IAppSyncAPIConfig {
    aws_appsync_graphqlEndpoint?: string;
    aws_appsync_authenticationType?: GRAPHQL_AUTH_MODE;
}

interface ISubscriptionResponse<T> {
    value: { data: T };
}

interface IUnsubscribe {
    unsubscribe: () => void;
}

export type TGraphQLSubscriptions = Observable<Record<string, unknown>> & IUnsubscribe;

const appSyncAuthFlowOptions = (options: IGraphqlOperationOptions) => {
  const presentationEmbedId: string = extractPathIdentifier(COMMON_PAGE_PATHS.PRESENTATION);
  return { ...options, authToken: authService.getJwtToken(presentationEmbedId) || 'Token' };
};

async function graphqlOperation<T> (options: IGraphqlOperationOptions): Promise<GraphQLResult<T>> {
  return (API.graphql(appSyncAuthFlowOptions(options))) as GraphQLResult<T>;
}

function graphqlSubscriptions<T> (
    options: IGraphqlOperationOptions,
    callback: (data: T) => void,
    retrySubscriptionFn: () => TGraphQLSubscriptions
): TGraphQLSubscriptions {
    /** WebSocket connection via resolver not support custom API endpoint. It always should point on endpoint that generated by AppSync*/
  return API.graphql(appSyncAuthFlowOptions(options)).subscribe({
    next: (response: ISubscriptionResponse<T>) => {
      const { value: { data }} = response;
      callback(data);
    },
    error: (error: IGraphQLSubscriptionError) => {
      const errorService: ErrorBoundaryStore = getErrorBoundaryStore();
      const { error: { errors: [extractedError] }} = error;
      if (extractedError.message === SUBSCRIPTION_CONNECTION_LOST_ERROR) {
        setTimeout(() => {
          --retryAttemptUntilSendErrorTrack;
          retrySubscriptionFn();
        });

        if(retryAttemptUntilSendErrorTrack === 0){
          getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.ERROR, MIXPANEL_EVENT_TYPES.ERROR_WEBSOCKET_CLOSE_PORT);
          retryAttemptUntilSendErrorTrack = DEFAULT_WS_CONNECTION_RETRY_VALUE;
        }

      } else {
        errorService.setSubscriptionError(error);
      }
    }
  });
}

export const graphqlClient = (function () {
  return {
    operation: graphqlOperation,
    subscriptions: graphqlSubscriptions,
  };
})();
